/**
 * isDropOp / dropOpDescription — pure helpers shared between
 * `commitDdlEdit`'s blast-radius gate (Slice H3) and the BlastRadiusPanel
 * (Slice H3) for classifying / labelling DSE2 `DdlEdit` arms whose effect
 * is to drop a dependent-bearing object or facet.
 *
 * The set of "drop ops" matches the destructive-guard scope in the PRD:
 * column / table / view / matview / sequence / type / schema / trigger /
 * routine / package / partition / login / user / role / database, plus
 * the per-constraint / per-index drops (`drop-primary-key`,
 * `drop-foreign-key`, `drop-unique-constraint`, `drop-check-constraint`,
 * `drop-named-default`, `drop-index`).
 *
 * The helper is intentionally string-only — no DSE2 import for the
 * predicate — so it stays trivially testable + cheap to call from inside
 * the commit pipeline.
 */
import type { DdlEdit } from '../../../sql-resolver/types';

const DROP_OPS = new Set<string>([
  'drop-column',
  'drop-view',
  'drop-materialized-view',
  'drop-sequence',
  'drop-type',
  'drop-schema',
  'drop-trigger',
  'drop-routine',
  'drop-package',
  'drop-partition',
  'drop-tablespace',
  'drop-filegroup',
  'drop-login',
  'drop-user',
  'drop-role',
  'drop-database',
  'drop-primary-key',
  'drop-foreign-key',
  'drop-unique-constraint',
  'drop-check-constraint',
  'drop-named-default',
  'drop-index',
]);

export function isDropOp(edit: DdlEdit): boolean {
  return DROP_OPS.has(edit.op);
}

/** Render-friendly summary of a drop edit, for the BlastRadiusPanel header.
 *  Falls back to a generic "Drop" label if the op is not in the matched set
 *  (e.g. a future drop-* op that ships before the panel knows about it). */
export function dropOpSummary(edit: DdlEdit): {
  readonly action: string;
  readonly target: string | null;
} {
  switch (edit.op) {
    case 'drop-column':
      return { action: 'Drop column', target: edit.columnName };
    case 'drop-primary-key':
      return { action: 'Drop primary key', target: null };
    case 'drop-foreign-key':
      return { action: 'Drop foreign key', target: edit.constraintName };
    case 'drop-unique-constraint':
      return { action: 'Drop unique constraint', target: edit.constraintName };
    case 'drop-check-constraint':
      return { action: 'Drop check constraint', target: edit.constraintName };
    case 'drop-named-default':
      return { action: 'Drop named default', target: edit.constraintName };
    case 'drop-index':
      return { action: 'Drop index', target: indexNameFromDrop(edit) };
    case 'drop-view':
      return { action: 'Drop view', target: nameFromQualified(edit) };
    case 'drop-materialized-view':
      return { action: 'Drop materialized view', target: nameFromQualified(edit) };
    case 'drop-sequence':
      return { action: 'Drop sequence', target: nameFromQualified(edit) };
    case 'drop-type':
      return { action: 'Drop type', target: nameFromQualified(edit) };
    case 'drop-schema':
      return { action: 'Drop schema', target: schemaNameFromDrop(edit) };
    case 'drop-trigger':
      return { action: 'Drop trigger', target: nameFromQualified(edit) };
    case 'drop-routine':
      return { action: 'Drop routine', target: nameFromQualified(edit) };
    case 'drop-package':
      return { action: 'Drop package', target: nameFromQualified(edit) };
    case 'drop-partition':
      return { action: 'Drop partition', target: partitionNameFromDrop(edit) };
    case 'drop-tablespace':
      return { action: 'Drop tablespace', target: tablespaceNameFromDrop(edit) };
    case 'drop-filegroup':
      return { action: 'Drop filegroup', target: filegroupNameFromDrop(edit) };
    case 'drop-login':
    case 'drop-user':
    case 'drop-role':
      return { action: 'Drop principal', target: principalNameFromDrop(edit) };
    case 'drop-database':
      return { action: 'Drop database', target: databaseNameFromDrop(edit) };
    default:
      return { action: 'Drop', target: null };
  }
}

type AnyEdit = Record<string, unknown>;

function nameFromQualified(edit: AnyEdit): string | null {
  const candidates = ['name', 'view', 'sequence', 'type', 'trigger', 'routine', 'package'];
  for (const key of candidates) {
    const v = edit[key];
    if (typeof v === 'string') return v;
    if (v && typeof v === 'object') {
      const raw = (v as AnyEdit).raw;
      const name = (v as AnyEdit).name;
      if (typeof raw === 'string') return raw;
      if (typeof name === 'string') return name;
    }
  }
  return null;
}

function indexNameFromDrop(edit: AnyEdit): string | null {
  const v = edit.indexName;
  if (typeof v === 'string') return v;
  return nameFromQualified(edit);
}

function schemaNameFromDrop(edit: AnyEdit): string | null {
  const v = edit.schemaName;
  if (typeof v === 'string') return v;
  return nameFromQualified(edit);
}

function partitionNameFromDrop(edit: AnyEdit): string | null {
  const v = edit.partitionName;
  if (typeof v === 'string') return v;
  return null;
}

function tablespaceNameFromDrop(edit: AnyEdit): string | null {
  const v = edit.tablespaceName;
  if (typeof v === 'string') return v;
  return null;
}

function filegroupNameFromDrop(edit: AnyEdit): string | null {
  const v = edit.filegroupName;
  if (typeof v === 'string') return v;
  return null;
}

function principalNameFromDrop(edit: AnyEdit): string | null {
  const v = edit.principalName;
  if (typeof v === 'string') return v;
  return nameFromQualified(edit);
}

function databaseNameFromDrop(edit: AnyEdit): string | null {
  const v = edit.databaseName;
  if (typeof v === 'string') return v;
  return null;
}
